import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { CreditBalance } from '../Credits/CreditBalance'
import {
  MemberActionCard,
  MemberBottomSheet,
  MemberConfirmSheet,
  MemberEmptyState,
  MemberSegmented,
} from './controls/MemberControlsPrimitives'
import { MemberNexusHero, MemberSurfaceFrame } from './controls/MemberNexusPrimitives'
import { useMemberLayout } from './MemberLayoutContext'
import { archiveAiChatSession, deleteAiChatSession, type AiChatSession } from '../../services/aiChatHistoryService'
import { trackPulseReportEvent } from '../../services/pulseReportTelemetry'
import { useLongPress } from '../../hooks/useLongPress'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'
import { KeyboardDock } from '../Common/KeyboardDock'
import { Button, Fab, IconButton, Pressable, TextInput } from '../Common'
import { useOverflowShadow } from '../../hooks/useOverflowShadow'

import {
  aiHistoryDisplayTitle,
  firstUserTurn,
  groupAiHistorySessions,
  lastAssistantTurn,
  liveAiHistorySessions,
  millis,
  sessionMatchesAiHistoryQuery,
  sourcePillsForSession,
} from './MemberAiHistoryTab.helpers'

type HistoryModeFilter = 'CREATE' | 'EXECUTE'

const relativeTime = (value: unknown, t: (key: NlsKey, values?: Record<string, string | number>) => string, now = Date.now()) => {
  const timestamp = millis(value)
  if (!timestamp) return t('NLSMemberAiHistoryTimeRecently')
  const minutes = Math.max(0, Math.floor((now - timestamp) / 60_000))
  if (minutes < 1) return t('NLSMemberAiHistoryTimeJustNow')
  if (minutes < 60) return t('NLSMemberAiHistoryTimeMinutesAgo', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('NLSMemberAiHistoryTimeHoursAgo', { count: hours })
  return t('NLSMemberAiHistoryTimeDaysAgo', { count: Math.floor(hours / 24) })
}

const modeLabel = (mode: AiChatSession['operatingMode'], t: (key: NlsKey) => string) =>
  mode === 'EXECUTE' ? t('NLSMemberAiHistoryModeExecute') : t('NLSMemberAiHistoryModeCreate')

export const MemberAiHistoryTab = ({
  sessions,
  activeSessionId,
  initialFilter = 'CREATE',
  walletCredits,
  onWalletClick,
  onOpenSession,
  onNewSession,
}: {
  sessions: AiChatSession[]
  activeSessionId?: string | null
  initialFilter?: HistoryModeFilter
  walletCredits: number
  onWalletClick?: () => void
  onOpenSession: (session: AiChatSession, source: 'row' | 'preview_sheet') => void
  onNewSession: (mode: AiChatSession['operatingMode']) => void
}) => {
  const { t } = useNls()
  const memberLayout = useMemberLayout()
  const [filter, setFilter] = useState<HistoryModeFilter>(initialFilter)
  const [search, setSearch] = useState('')
  const [preview, setPreview] = useState<AiChatSession | null>(null)
  const [busySessionId, setBusySessionId] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<{
    session: AiChatSession
    action: 'archive' | 'delete'
  } | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  useOverflowShadow(listRef)
  const longPressSessionRef = useRef<AiChatSession | null>(null)
  const suppressClickRef = useRef(false)

  const [mountTime] = useState(() => Date.now())
  const liveSessions = useMemo(() => liveAiHistorySessions(sessions), [sessions])
  const counts = useMemo(() => ({
    CREATE: liveSessions.filter((session) => session.operatingMode === 'CREATE').length,
    EXECUTE: liveSessions.filter((session) => session.operatingMode === 'EXECUTE').length,
  }), [liveSessions])

  const groups = useMemo(() => {
    const filtered = liveSessions
      .filter((session) => session.operatingMode === filter)
      .filter((session) => sessionMatchesAiHistoryQuery(session, search))
    return groupAiHistorySessions(filtered, mountTime)
  }, [filter, liveSessions, mountTime, search])

  const longPress = useLongPress({ ms: 450, driftPx: 6, onLongPress: () => {
    const session = longPressSessionRef.current
    if (!session) return
    suppressClickRef.current = true
    setPreview(session)
    trackPulseReportEvent({ name: 'ai_history_preview_opened', props: { sessionId: session.id, kind: session.operatingMode, source: 'long_press' } })
  } })

  const startLongPress = (event: ReactPointerEvent, session: AiChatSession) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    suppressClickRef.current = false
    longPressSessionRef.current = session
    longPress.onPointerDown(event)
  }

  const runConfirmedSessionAction = async () => {
    if (!pendingAction) return
    const { session, action } = pendingAction
    setBusySessionId(session.id)
    try {
      if (action === 'archive') await archiveAiChatSession(session.id)
      else await deleteAiChatSession(session.id)
      setPendingAction(null)
    } finally {
      setBusySessionId(null)
    }
  }

  const openSession = (session: AiChatSession, source: 'row' | 'preview_sheet') => {
    trackPulseReportEvent({ name: 'ai_history_opened', props: { sessionId: session.id, kind: session.operatingMode, source } })
    onOpenSession(session, source)
  }

  const controls = (
    <div className="memberAiHistoryControls">
      <MemberSegmented
        value={filter}
        ariaLabel={t('NLSMemberAiHistoryFilterAria')}
        onChange={setFilter}
        options={[
          { key: 'CREATE', label: t('NLSMemberAiHistoryModeCreate'), count: counts.CREATE, testId: 'member-ai-history-filter-create' },
          { key: 'EXECUTE', label: t('NLSMemberAiHistoryModeExecute'), count: counts.EXECUTE, testId: 'member-ai-history-filter-execute' },
        ]}
      />
      <KeyboardDock label={t('NLSMemberAiHistorySearchLabel')}>
        <label className="memberAiSearch">
          <TextInput
            data-text-overlay="true"
            type="search"
            leadingIcon={<SearchRoundedIcon fontSize="small" />}
            value={search}
            onChange={setSearch}
            placeholder={t('NLSMemberAiHistorySearchLabel')}
            block
          />
        </label>
      </KeyboardDock>
    </div>
  )

  return (
    <div className="memberAiHistoryPanel" data-ui-name="UI-MPAI-HIST">
      <MemberSurfaceFrame
        className="memberAiHistoryFrame aiScrollFade"
        testId="member-ai-history-list"
        scrollRef={listRef}
      >
        <MemberNexusHero
          eyebrow={t('NLSMemberAiEyebrow')}
          title={t('NLSMemberAiHistoryTitle')}
          rightSlot={(
            <div className="memberAiHeroActions">
              {/* P2: ⋯ hidden on History. No date-filter, bulk archive/delete, or
               * clear-history handler exists at this component boundary. */}
              <Pressable className="memberNexusHero__chip memberTapRipple" onClick={() => onNewSession(filter)}>
                <span>{modeLabel(filter, t)} </span>
                <strong>{counts[filter]}</strong>
              </Pressable>
              <CreditBalance value={walletCredits} variant="bare" scope="workspace" label={null} className="memberAiWalletChip" onClick={onWalletClick} title={t('NLSMemberAiHistoryWorkspaceCreditsTitle')} />
            </div>
          )}
        />
        {memberLayout?.bottomAddonTarget ? null : controls}
        {memberLayout?.bottomAddonTarget ? createPortal(controls, memberLayout.bottomAddonTarget) : null}
        {groups.length ? groups.map((group) => (
          <section className="memberAiHistoryGroup" key={group.label}>
            <h3>{t(group.labelKey)}</h3>
            {group.sessions.map((session) => {
              const pills = sourcePillsForSession(session)
              const turnsText = session.turns.length === 1
                ? t('NLSMemberAiHistoryTurnsCountOne', { count: session.turns.length })
                : t('NLSMemberAiHistoryTurnsCount', { count: session.turns.length })
              return (
                <div className={`memberAiHistoryRow ${activeSessionId === session.id ? 'isActive' : ''}`} key={session.id}>
                  <div
                    className="memberAiHistoryRowHit"
                    onPointerDown={(event) => startLongPress(event, session)}
                    onPointerMove={longPress.onPointerMove}
                    onPointerUp={longPress.onPointerUp}
                    onPointerCancel={longPress.onPointerCancel}
                  >
                    <MemberActionCard
                      title={aiHistoryDisplayTitle(session.title)}
                      sub={t('NLSMemberAiHistoryAriaSessionSub', {
                        mode: modeLabel(session.operatingMode, t),
                        turns: turnsText,
                        time: relativeTime(session.lastMessageAt ?? session.updatedAt, t),
                      })}
                      footer={pills.length ? (
                        <span className="memberAiSourcePills">
                          {pills.map(({ id, label, Icon }) => (
                            <em key={id}><Icon fontSize="inherit" />{label}</em>
                          ))}
                        </span>
                      ) : null}
                      actions={(
                        <>
                          <IconButton
                            variant="tertiary"
                            size="md"
                            disabled={busySessionId === session.id}
                            onClick={() => setPendingAction({ session, action: 'archive' })}
                            label={t('NLSMemberAiHistoryActionArchive')}
                            icon={<ArchiveRoundedIcon fontSize="inherit" />}
                          />
                          <IconButton
                            variant="tertiary"
                            size="md"
                            disabled={busySessionId === session.id}
                            onClick={() => setPendingAction({ session, action: 'delete' })}
                            label={t('NLSMemberAiHistoryActionDelete')}
                            icon={<DeleteOutlineRoundedIcon fontSize="inherit" />}
                          />
                        </>
                      )}
                      className="memberAiHistoryRowCard"
                      onClick={() => {
                        if (suppressClickRef.current) {
                          suppressClickRef.current = false
                          return
                        }
                        openSession(session, 'row')
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </section>
        )) : (
          <MemberEmptyState
            icon={<HistoryRoundedIcon />}
            title={t('NLSMemberAiHistoryEmptyTitle')}
            hint={t('NLSMemberAiHistoryEmptyHint')}
          />
        )}
      </MemberSurfaceFrame>
      <Fab
        icon={<AutoAwesomeRoundedIcon fontSize="small" />}
        label={t('NLSMemberAiHistoryFabAriaLabel')}
        className="memberGradientFab memberAiFloatingFab"
        onClick={() => onNewSession(filter)}
      />
      <MemberBottomSheet
        open={Boolean(preview)}
        title={preview?.title ? aiHistoryDisplayTitle(preview.title) : t('NLSMemberAiHistoryPreviewTitle')}
        onClose={() => setPreview(null)}
        testId="member-ai-history-preview-sheet"
      >
        {preview ? (
          <div className="memberAiPreview">
            <p><strong>{t('NLSMemberAiHistoryPreviewFirstAsk')}</strong>{firstUserTurn(preview) || t('NLSMemberAiHistoryPreviewNoUserTurn')}</p>
            <p><strong>{t('NLSMemberAiHistoryPreviewLastAnswer')}</strong>{lastAssistantTurn(preview) || t('NLSMemberAiHistoryPreviewNoAnswer')}</p>
            <Button variant="primary" size="lg" onClick={() => { openSession(preview, 'preview_sheet'); setPreview(null) }}>
              {t('NLSMemberAiHistoryPreviewOpenAction')}
            </Button>
          </div>
        ) : null}
      </MemberBottomSheet>
      <MemberConfirmSheet
        open={Boolean(pendingAction)}
        title={pendingAction?.action === 'delete'
          ? t('NLSMemberAiHistoryDeleteConfirmTitle')
          : t('NLSMemberAiHistoryArchiveConfirmTitle')}
        body={pendingAction?.action === 'delete'
          ? t('NLSMemberAiHistoryDeleteConfirmBody')
          : t('NLSMemberAiHistoryArchiveConfirmBody')}
        confirmLabel={pendingAction?.action === 'delete'
          ? t('NLSMemberAiHistoryDeleteConfirmAction')
          : t('NLSMemberAiHistoryArchiveConfirmAction')}
        cancelLabel={t('NLSCommonActionCancel')}
        tone={pendingAction?.action === 'delete' ? 'danger' : 'neutral'}
        busy={Boolean(busySessionId)}
        onConfirm={() => void runConfirmedSessionAction()}
        onCancel={() => setPendingAction(null)}
        testId="member-ai-history-confirm-sheet"
      />
    </div>
  )
}

